import { useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { openUrlInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { useCallback, useRef, useState } from "react";

import { type InterviewType } from "../../WorkerShifts/api/useFetchPaginatedInterviews";

interface AddInterviewToCalendarParams {
  placementId: string;
  interviewId: string;
  interviewType: InterviewType;
  icsFileUrl: string;
}

interface UseAddInterviewToCalendarResult {
  addToCalendar: (params: AddInterviewToCalendarParams) => Promise<void>;
  isAddingToCalendar: boolean;
}

export function useAddInterviewToCalendar(): UseAddInterviewToCalendarResult {
  const { showErrorToast } = useToast();
  const [isAddingToCalendar, setIsAddingToCalendar] = useState(false);
  const isAddingRef = useRef(false);

  const addToCalendar = useCallback(
    async (params: AddInterviewToCalendarParams) => {
      if (isAddingRef.current) {
        return;
      }

      const { placementId, interviewId, interviewType, icsFileUrl } = params;

      isAddingRef.current = true;
      setIsAddingToCalendar(true);

      try {
        logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ADD_TO_CALENDAR_CLICKED, {
          placementId,
          interviewId,
          interviewType,
        });

        await openUrlInBrowser(icsFileUrl, "interview-add-to-calendar");
      } catch {
        showErrorToast("Unable to add to calendar. Please try again.");
      } finally {
        isAddingRef.current = false;
        setIsAddingToCalendar(false);
      }
    },
    [showErrorToast]
  );

  return { addToCalendar, isAddingToCalendar };
}
